<template>
  <div
    ref="text-fit-container"
    class="text-fit-container"
  >
    <slot/>
  </div>
</template>

<script>
import {getConsole, getMockConsole} from "@seriousgamesinteractive/utils/console/getConsole";

export default {
  name: 'TextFit',
  props: {
    initialSize: {
      type: Number,
      required: false,
      default: 40,
    },
    sizeUnit: {
      type: String,
      required: false,
      default: 'pt',
    },
    step: {
      type: Number,
      required: false,
      default: 3,
    },
    minSize: {
      type: Number,
      required: false,
      default: 5,
    },
    debug: {
      type: Boolean,
      required: false,
      default: false,
    },
    debugStepTime: {
      type: Number,
      required: false,
      default: 0,
    },
    fitWidth: {
      type: Number,
      required: false,
      default: null,
    },
    fitHeight: {
      type: Number,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      observer: null,
      observerMutationCount: 0,

      doObserve: true,

      resizeCount: 0,

      console: this.debug ? getConsole('TextFit', '#88aaaa') : getMockConsole(),
    };
  },
  emits: ['render'],
  watch: {
    observerMutationCount() {
      this.fitText();
    },
  },
  mounted() {
    const MutationObserver = window.MutationObserver || window.WebKitMutationObserver || window.MozMutationObserver;

    this.observer = new MutationObserver(() => {
      if (this.doObserve) {
        this.observerMutationCount += 1;
      }
    });

    const htmlObserverOptions = {
      attributes: true,
      childList: true,
      characterData: true,
      subtree: true,
    };

    // Setup the observer to observe when slot changes
    //console.log('MarkdownHoverTextCompiler', this.$refs['markdown-text-compiler']);
    this.observer.observe(this.$refs['text-fit-container'], htmlObserverOptions);

    this.fitText();
  },
  beforeUnmount() {
    // Clean up, disconnect observer
    this.observer.disconnect();

    this.observer = null;
  },
  methods: {
    setFontSize(fontSize) {
      if (fontSize < this.minSize) {
        fontSize = this.minSize;
      }

      this.fontSize = fontSize;
      this.textFitContainer.style.fontSize = fontSize + this.sizeUnit;
    },
    fitText() {
      if (!this.doObserve) {
        return;
      }

      if (this.debug) {
        this.console.log('fitText()');
      }

      this.startStep();
    },
    startStep() {
      this.doObserve = false;

      this.textFitContainer = this.$refs['text-fit-container'];
      this.textFitContainerParent = this.textFitContainer.parentElement;

      this.fontSize = this.initialSize;

      this.textFitContainer.style.fontSize = this.fontSize + this.sizeUnit;

      this.parentRect = this.getRoundedRect(this.textFitContainerParent.getBoundingClientRect());

      if (this.fitWidth) {
        this.parentRect.width = this.fitWidth;
      }

      if (this.fitHeight) {
        this.parentRect.height = this.fitHeight;
      }

      this.resizeCount = 0;

      if (this.debug) {
        this.console.log('startStep()');
      }

      requestAnimationFrame(() => {
        this.nextStep();
      });
    },
    endStep() {
      if (this.debug) {
        this.console.log('endStep()');
      }

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.doObserve = true;
        });
      });
    },
    getRoundedRect(rect) {
      const newRect = {};

      for (const key in rect) {
        newRect[key] = Math.round(rect[key]);
      }

      return newRect;
    },
    nextStep() {
      const textRect = this.getRoundedRect(this.textFitContainer.getBoundingClientRect());

      if (this.debug) {
        console.log('nextStep', {
          textRect,
          textFitContainer: this.textFitContainer,
        });
      }

      if (textRect.width <= this.parentRect.width && textRect.height <= this.parentRect.height) {
        if (this.debug) {
          this.console.log(`Successfully fit within parentRect`, {
            fontSize: this.fontSize,
            textRect,
            parentRect: this.parentRect
          });
        }

        this.endStep();

        return;
      }

      this.setFontSize(this.fontSize - this.step);

      if (this.fontSize <= this.minSize) {
        if (this.debug) {
          this.console.log(`Could not decrease font size below ${this.minSize}`);
        }

        this.setFontSize(this.minSize);

        this.endStep();

        return;
      }

      this.resizeCount += 1;

      if (this.debug) {
        this.console.log({
          'resizeCount': this.resizeCount,

          parentRectWidth: this.parentRect.width,
          parentRectHeight: this.parentRect.height,

          textRectWidth: textRect.width,
          textRectHeight: textRect.height,

          'fontSize': this.fontSize,
          'textFitContainerParent': this.textFitContainerParent,
          'textFitContainer': this.textFitContainer,
        })
      }

      if (this.debugStepTime > 0) {
        setTimeout(() => {
          this.nextStep();
        }, this.debugStepTime)
      } else {
        this.nextStep();
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.text-fit-container {
  width: 100%;
  height: fit-content;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  line-height: 120%;
}
</style>
